import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import Input from "../common/Input";
import { partnerService } from "../../services/partners";
import toast from "react-hot-toast";

const PartnerForm = ({ partner, onSuccess }) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: partner || {},
  });
  const [loading, setLoading] = useState(false);

  const partnerType = watch("partnerType");

  useEffect(() => {
    if (partner) {
      reset({
        ...partner,
        agreementStart: partner.agreementStart?.split("T")[0] || "",
        agreementEnd: partner.agreementEnd?.split("T")[0] || "",
        "bankDetails.accountName": partner.bankDetails?.accountName || "",
        "bankDetails.accountNumber": partner.bankDetails?.accountNumber || "",
        "bankDetails.bankName": partner.bankDetails?.bankName || "",
        "bankDetails.ifscCode": partner.bankDetails?.ifscCode || "",
      });
    } else {
      reset({});
    }
  }, [partner, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      ...data,
      bankDetails: {
        accountName: data["bankDetails.accountName"] || data.bankDetails?.accountName || "",
        accountNumber: data["bankDetails.accountNumber"] || data.bankDetails?.accountNumber || "",
        bankName: data["bankDetails.bankName"] || data.bankDetails?.bankName || "",
        ifscCode: data["bankDetails.ifscCode"] || data.bankDetails?.ifscCode || "",
      },
    };
    delete payload["bankDetails.accountName"];
    delete payload["bankDetails.accountNumber"];
    delete payload["bankDetails.bankName"];
    delete payload["bankDetails.ifscCode"];

    try {
      if (partner) {
        await partnerService.update(partner._id, payload);
        toast.success("Partner updated");
      } else {
        await partnerService.create(payload);
        toast.success("Partner created");
      }
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      reset();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Name"
        {...register("name", { required: "Name is required" })}
        error={errors.name?.message}
      />
      <Input
        label="Email"
        type="email"
        {...register("email", { required: "Email is required" })}
        error={errors.email?.message}
      />
      <Input
        label="Phone"
        {...register("phone", { required: "Phone is required" })}
        error={errors.phone?.message}
      />
      <Input label="Company" {...register("company")} />
      <div>
        <label className="block text-sm font-medium mb-1">Partner Type</label>
        <select
          {...register("partnerType", { required: "Partner type is required" })}
          className="w-full border rounded p-2"
        >
          <option value="">Select Partner Type</option>
          <option value="commission">Commission</option>
          <option value="revenue_share">Revenue Share</option>
          <option value="fixed">Fixed</option>
          <option value="hybrid">Hybrid</option>
        </select>
        {errors.partnerType && <p className="mt-1 text-xs text-red-500">{errors.partnerType.message}</p>}
      </div>
      {(partnerType === "commission" || partnerType === "revenue_share" || partnerType === "hybrid") && (
        <Input
          label="Commission Rate (%)"
          type="number"
          {...register("commissionRate", { min: 0, max: 100 })}
        />
      )}
      {(partnerType === "fixed" || partnerType === "hybrid") && (
        <Input
          label="Fixed Amount"
          type="number"
          {...register("fixedAmount")}
        />
      )}
      <div>
        <label className="block text-sm font-medium mb-1">Status</label>
        <select
          {...register("status")}
          className="w-full border rounded p-2"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>
      <Input
        label="Agreement Start"
        type="date"
        {...register("agreementStart")}
      />
      <Input
        label="Agreement End"
        type="date"
        {...register("agreementEnd")}
      />
      <Input label="Address" {...register("address")} />

      <div className="border-t pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Bank Details</h4>
        <div className="space-y-4">
          <Input label="Account Name" {...register("bankDetails.accountName")} />
          <Input label="Account Number" {...register("bankDetails.accountNumber")} />
          <Input label="Bank Name" {...register("bankDetails.bankName")} />
          <Input label="IFSC Code" {...register("bankDetails.ifscCode")} />
        </div>
      </div>

      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : partner ? "Update" : "Create"}
      </Button>
    </form>
  );
};

export default PartnerForm;
